import { LiveData } from '@toeverything/infra';

import { getAudioHandlerType, TranscriptionSource } from '../hooks/transcription-source-config';
import type { IAudioHandler } from './audio-handler-interface';
import { BrowserAudioHandler } from './browser-audio-handler';
import { TauriAudioHandler } from './tauri-audio-handler';
import type { TranscriptionService } from './transcription-service';
import { TranscriptionStatus } from './transcription-service';

export class AudioHandler implements IAudioHandler {
  public readonly audioDevices$ = new LiveData<MediaDeviceInfo[]>([]);
  public readonly selectedDeviceId$ = new LiveData<string | undefined>(undefined);

  private tauriHandler: TauriAudioHandler | null = null;
  private browserHandler: BrowserAudioHandler | null = null;
  private currentHandler: IAudioHandler | null = null;

  private isSessionActive: boolean = false;
  private currentSource: TranscriptionSource = TranscriptionSource.NONE;

  // Subscription cleanup
  private transcriptionSourceUnlisten: (() => void) | undefined;
  private transcriptionStateUnlisten: (() => void) | undefined;

  constructor(
    private readonly transcriptionService: TranscriptionService
  ) {
    this.initializeHandlers();
    this.setupSubscriptions();
  }

  private initializeHandlers(): void {
    if (environment.isTauri) {
      this.tauriHandler = new TauriAudioHandler(this.transcriptionService);
    }
    // BrowserAudioHandler is always available — on desktop it powers the
    // experimental Google Speech gateway path via Socket.IO.
    this.browserHandler = new BrowserAudioHandler(this.transcriptionService);
  }

  private setupSubscriptions(): void {
    const sourceSubscription = this.transcriptionService.transcriptionSource$.subscribe(
      (source: TranscriptionSource) => {
        this.updateCurrentHandler(source);
      }
    );
    this.transcriptionSourceUnlisten = () => sourceSubscription.unsubscribe();

    const statusSubscription = this.transcriptionService.transcriptionStatus$.subscribe(
      (status: TranscriptionStatus) => {
        this.handleStatusChange(status);
      }
    );
    this.transcriptionStateUnlisten = () => statusSubscription.unsubscribe();
  }

  private updateCurrentHandler(source: TranscriptionSource): void {
    const handlerType = getAudioHandlerType(source);

    if (this.currentSource === source && this.currentHandler) {
      return;
    }

    let newHandler: IAudioHandler | null = null;
    switch (handlerType) {
      case 'tauri':
        newHandler = this.tauriHandler;
        break;
      case 'deepgram':
      case 'browser':
      default:
        newHandler = this.browserHandler;
        break;
    }

    // Stop the old handler if actively recording and switching handlers
    if (this.isSessionActive && this.currentHandler && this.currentHandler !== newHandler) {
      this.isSessionActive = false;
      this.currentHandler.stop().catch(console.error);
    }

    this.currentHandler = newHandler;
    this.currentSource = source;
  }

  private handleStatusChange(status: TranscriptionStatus): void {
    if (!this.currentHandler) return;

    switch (status) {
      case TranscriptionStatus.RECORDING:
        if (!this.isSessionActive) {
          this.isSessionActive = true;
          this.currentHandler.start().catch(error => {
            console.error('[AudioHandler] Failed to start:', error);
            this.isSessionActive = false;
          });
        }
        break;
      case TranscriptionStatus.PAUSED:
        // Treat paused as a non-recording state; ensure audio pipeline is stopped
        if (this.isSessionActive) {
          this.isSessionActive = false;
          this.currentHandler.stop().catch(console.error);
        }
        break;
      case TranscriptionStatus.INACTIVE:
        if (this.isSessionActive) {
          this.isSessionActive = false;
          this.currentHandler.stop().catch(console.error);
        }
        break;
    }
  }
  public async start(): Promise<void> {
    if (this.currentHandler) {
      await this.currentHandler.start();
    }
  }

  public async stop(): Promise<void> {
    if (this.currentHandler) {
      await this.currentHandler.stop();
    }
  }

  public getMediaStream(): MediaStream | null {
    return this.currentHandler?.getMediaStream() || null;
  }

  public dispose(): void {
    if (this.isSessionActive && this.currentHandler) {
      this.isSessionActive = false;
      this.currentHandler.stop().catch(console.error);
    }

    if (this.transcriptionSourceUnlisten) {
      this.transcriptionSourceUnlisten();
      this.transcriptionSourceUnlisten = undefined;
    }

    if (this.transcriptionStateUnlisten) {
      this.transcriptionStateUnlisten();
      this.transcriptionStateUnlisten = undefined;
    }

    // Dispose all handlers
    this.tauriHandler?.dispose();
    this.browserHandler?.dispose();

    this.tauriHandler = null;
    this.browserHandler = null;
    this.currentHandler = null;
  }
}
